import { db, seedBuiltInMeasurementTypes, type LiftXDatabase } from './db';
import { seedCatalogueIfNeeded } from './seed/seedCatalogue';
import { createSettingsRepository } from './repositories/settingsRepository';

/**
 * Clears every account-owned table on this device and re-seeds the
 * account-independent defaults (built-in measurement types, the exercise
 * catalogue) — "Multi-user identity architecture" review, Phase G-A, and the
 * Phase G race-condition fix design below.
 *
 * `db` is one shared Dexie instance per browser origin, not partitioned per
 * account — `AuthProvider.tsx`'s `logout()`/`revalidate()` are the two
 * callers, and both already independently established this in their own
 * comments for the narrower case of the sync cursor. Without this function,
 * a second account authenticating on a device that still has a previous
 * account's local data would have its very first sync cycle push that stale
 * data under its own `user_id` — the cross-account leak this closes.
 *
 * Deliberately does NOT touch `settings` or `meta` — `appearance` and the
 * device's own `schemaVersion`/`createdAt` provenance are device properties,
 * not account data, and are safe (and desirable) to carry across an account
 * switch on the same device. `settings` is also where `localResetState`
 * (below) itself lives, which is exactly why it has to stay untouched by the
 * clear. Callers remain responsible for clearing any account-specific
 * `settings` keys themselves (`AuthProvider` already does, for
 * `lastVerified`/`syncCursor`) and for recording *ownership* — see this
 * function's own "what this function deliberately does not do" note below.
 *
 * A plain sequential loop inside the transaction, not `Promise.all` — keeps
 * this unambiguously safe under Dexie's transaction-scope tracking rather
 * than relying on native `Promise.all` interacting correctly with it.
 *
 * ## Coalescing (in-flight promise)
 *
 * Concurrent callers against the SAME `LiftXDatabase` instance join the one
 * true in-flight reset rather than racing independent clears — the exact
 * pattern `sync/syncEngine.ts`'s own `activeCycle` already uses for
 * `runSyncCycle()`. This is what makes it safe for both `logout()` (fired
 * immediately, unawaited) and `revalidate()`'s own mismatch-detected call
 * (awaited, potentially moments later) to both call this function without
 * either duplicating the other's work or racing it: whichever call arrives
 * second simply awaits the same promise the first call is already driving.
 * Keyed by database instance (a `WeakMap`, not a single module-level
 * promise) so that test suites constructing their own isolated
 * `LiftXDatabase` instances never share state with each other or with the
 * real `db` singleton. A rejected reset is removed from this cache in a
 * `finally`, so a failed attempt never permanently wedges the next call
 * behind a promise that can only ever reject again — the next call starts a
 * genuinely fresh attempt.
 *
 * ## Durable state (`SETTING_KEYS.localResetState`)
 *
 * The in-flight promise above only exists in memory — gone the instant the
 * tab reloads or closes. `localResetState` is the durable counterpart,
 * written to `settings` (see above for why that table is safe to use here)
 * before the clear begins and updated once it settles, so a crash or refresh
 * mid-reset leaves durable evidence rather than silence. `AuthProvider.tsx`'s
 * `revalidate()` treats anything other than a freshly-succeeded `'idle'` as
 * "not yet safe," and `sync/syncEngine.ts`'s `runSyncCycle()` independently
 * refuses to push or pull unless it reads `'idle'` — see each of those files'
 * own comments for exactly how they use it.
 *
 * ## Known limitation: multiple tabs
 *
 * The coalescing above is per-JS-module-instance (a `WeakMap`, reset on
 * every page load), and the durable marker is per-database, not per-tab —
 * so within ONE tab's own causal chain (its own logout, immediately
 * followed by a login on the same tab, or a reload mid-reset), the
 * guarantees above are unconditional. They do NOT extend to a genuinely
 * simultaneous account switch across two open tabs on the same origin — e.g.
 * Tab 1 calls `logout()` while Tab 2 is still an active, authenticated
 * session for the account being logged out of. IndexedDB itself is shared
 * across tabs and Dexie's own transaction-level locking still applies (so a
 * concurrent read/write cannot observe a torn, half-cleared table), but
 * nothing here signals Tab 2 that a reset happened, and Tab 2's own next
 * sync cycle would still pass its own gate check (its in-memory `status`
 * hasn't changed) and could push whatever it still has in memory. Closing
 * this fully would need explicit cross-tab signaling (`BroadcastChannel` or
 * the Web Locks API) — deliberately not added here per the fix design's own
 * "prefer the smallest robust mechanism" instruction; this is a known,
 * documented gap, not a silently assumed non-issue.
 *
 * ## What this function deliberately does NOT do
 *
 * It never writes `localDataOwner` (`SETTING_KEYS.localDataOwner`) — whose
 * data the tables become once cleared is an authentication-lifecycle
 * decision, not a database-lifecycle one, and `resetLocalDatabase()` itself
 * has no reason to know or care which account (if any) is about to occupy
 * the freshly-cleared tables. `AuthProvider.tsx`'s `revalidate()` is the only
 * place that ever calls `settingsRepository.setLocalDataOwner()`, and only
 * once this function has *already* resolved successfully — folding
 * ownership assignment into this function would make the local-database
 * layer depend on the identity of "whoever happens to be authenticating,"
 * which is exactly the kind of auth-into-data-layer coupling this design
 * avoids (see the fix design's own architectural-ownership discussion).
 */
export function resetLocalDatabase(
  database: LiftXDatabase = db,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const existing = inFlightResets.get(database);
  if (existing !== undefined) return existing;

  const attempt = performReset(database, fetchImpl).finally(() => {
    inFlightResets.delete(database);
  });
  inFlightResets.set(database, attempt);
  return attempt;
}

const inFlightResets = new WeakMap<LiftXDatabase, Promise<void>>();

async function performReset(database: LiftXDatabase, fetchImpl: typeof fetch): Promise<void> {
  const settings = createSettingsRepository(database);
  const tables = [
    database.exercises,
    database.userMedia,
    database.plans,
    database.planDays,
    database.planDaySchedules,
    database.planBlocks,
    database.planExercises,
    database.planSets,
    database.progressionRules,
    database.sessions,
    database.sessionBlocks,
    database.sessionExercises,
    database.sessionSets,
    database.cardioSegments,
    database.personalRecords,
    database.measurementTypes,
    database.bodyMeasurements,
    database.backupRecords,
    database.historyEditAudit,
    database.exerciseUserState,
  ];

  await settings.setLocalResetState('in-progress');

  try {
    await database.transaction('rw', tables, async () => {
      for (const table of tables) {
        await table.clear();
      }
    });

    // Re-seed the account-independent defaults so the device is immediately
    // usable again, rather than looking broken until some other trigger
    // happens to re-seed it. Catalogue seeding needs a fetch, so it's
    // best-effort — a failure here is recoverable (the existing Settings →
    // Catalogue "repair/verify" control, `CatalogueProvider`'s own retry),
    // not something that should make the whole reset attempt fail (or read
    // as `'failed'` below) — only the clear itself, and the built-in
    // measurement seed, are load-bearing for `localResetState`.
    await seedBuiltInMeasurementTypes(database);
    try {
      await seedCatalogueIfNeeded({ database, fetchImpl });
    } catch {
      // Best-effort — see comment above.
    }

    await settings.setLocalResetState('idle');
  } catch (error) {
    // Never swallowed: `localResetState` durably records the failure (so
    // `runSyncCycle()` and the next `revalidate()` both refuse to trust
    // this device's data), and the error is rethrown so the caller
    // (`AuthProvider.tsx`'s `settleLocalDatabaseFor`) sees it and can react
    // — a caller that only fires this and forgets (`logout()`) simply never
    // observes the rejection, which is fine: it isn't the one responsible
    // for deciding what happens next.
    await settings.setLocalResetState('failed').catch(() => undefined);
    throw error;
  }
}
